<template>
  <button
    type="button"
    class="bui-ter-btn"
    :class="{ 'icon-only': !hasLabel, active, disabled }"
    :disabled="disabled"
    :aria-pressed="active || undefined"
    :aria-expanded="ariaExpanded"
    :aria-controls="ariaControls"
    :title="title"
    :aria-label="ariaLabel || (hasLabel ? undefined : title)"
    :data-testid="dataTestId"
    @click="onClick"
  >
    <span v-if="$slots.icon" class="ico">
      <slot name="icon" />
    </span>
    <span v-if="hasLabel" class="label">
      <slot>{{ label }}</slot>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue';

const props = withDefaults(
  defineProps<{
    label?: string;
    title?: string;
    ariaLabel?: string;
    ariaExpanded?: boolean;
    ariaControls?: string;
    active?: boolean;
    disabled?: boolean;
    dataTestId?: string;
  }>(),
  { active: false, disabled: false },
);

const slots = useSlots();
const emit = defineEmits<{ click: [event: MouseEvent] }>();

const hasLabel = computed(() => !!(props.label || slots.default));

function onClick(e: MouseEvent) {
  if (props.disabled) return;
  emit('click', e);
}
</script>

<style lang="scss" scoped>
.bui-ter-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: var(--bui-ter-h, 52px);
  padding: 0 var(--bui-ter-pad, 14px);
  background: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
  border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
  border-radius: var(--bui-ter-radius, 8px);
  color: var(--bui-ter-text, #ffd9e6);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 17px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 120ms ease;

  &.icon-only {
    width: var(--bui-ter-h, 52px);
    padding: 0;
    justify-content: center;
  }

  .ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--bui-ter-icon, #fffcfd);
    line-height: 0;

    :deep(svg) {
      width: 24px;
      height: 24px;
      display: block;
    }
  }

  .label {
    display: inline-flex;
    align-items: center;
    padding: 0 2px;
  }

  &:hover:not(.disabled) {
    background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
  }

  &.active {
    background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
  }

  &:focus-visible {
    outline: 2px solid var(--bui-ter-icon, #fffcfd);
    outline-offset: 2px;
  }

  &.disabled {
    opacity: 0.5;
    cursor: default;
  }
}

@media (max-width: 768px) {
  .bui-ter-btn {
    font-size: 12px;
    line-height: 15px;
  }

  .bui-ter-btn .ico :deep(svg) {
    width: 18px;
    height: 18px;
  }
}
</style>
